import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel } from "@/utils/debugUtils";

/**
 * Verifies if a round exists in the database and creates it if it doesn't
 */
export const verifyRoundExists = async (
  gameId: string,
  roundNumber: number
): Promise<boolean> => {
  try {
    // Check if round exists in database
    const { data: roundData, error: roundError } = await supabase
      .from("rounds")
      .select("id")
      .eq("game_id", gameId)
      .eq("round_number", roundNumber)
      .maybeSingle();
    
    if (roundError) {
      debugLog(DebugLevel.ERROR, "databaseOperations", `Error checking if round ${roundNumber} exists:`, roundError);
      return false;
    }
    
    // If round doesn't exist, create it
    if (!roundData) {
      debugLog(DebugLevel.INFO, "databaseOperations", `Round ${roundNumber} not found, creating it`);
      
      const { error: createError } = await supabase
        .from("rounds")
        .insert({ 
          game_id: gameId, 
          round_number: roundNumber
        });
        
      if (createError) {
        debugLog(DebugLevel.ERROR, "databaseOperations", `Failed to create round ${roundNumber}:`, createError);
        return false;
      }
      
      debugLog(DebugLevel.INFO, "databaseOperations", `Successfully created round ${roundNumber}`);
    }
    
    return true;
  } catch (error) {
    debugLog(DebugLevel.ERROR, "databaseOperations", `Exception verifying round ${roundNumber}:`, error);
    return false;
  }
};

/**
 * Retrieves round data from the database
 */
export const fetchRoundData = async (
  gameId: string,
  roundNumber: number
) => {
  try {
    const { data, error } = await supabase
      .from("rounds")
      .select("user_word, ai_word, ai_explanation")
      .eq("game_id", gameId)
      .eq("round_number", roundNumber)
      .maybeSingle();
      
    if (error) {
      debugLog(DebugLevel.ERROR, "databaseOperations", `Error fetching round ${roundNumber} data:`, error);
      return { error, data: null };
    }
    
    return { error: null, data };
  } catch (error) {
    debugLog(DebugLevel.ERROR, "databaseOperations", `Exception fetching round ${roundNumber} data:`, error);
    return { error, data: null };
  }
};

/**
 * Updates round data in the database
 */
export const updateRoundData = async (
  gameId: string,
  roundNumber: number,
  userWord: string | null, 
  aiWord: string | null,
  aiExplanation: string | null
) => {
  try {
    // Build update object with only the fields that have values
    const updateData: Record<string, any> = {};
    if (userWord !== null) updateData.user_word = userWord;
    if (aiWord !== null) updateData.ai_word = aiWord;
    if (aiExplanation !== null) updateData.ai_explanation = aiExplanation;
    
    // Only proceed if there's something to update
    if (Object.keys(updateData).length === 0) {
      return { error: null };
    }
    
    const { error } = await supabase
      .from("rounds")
      .update(updateData)
      .eq("game_id", gameId)
      .eq("round_number", roundNumber);
      
    if (error) {
      debugLog(DebugLevel.ERROR, "databaseOperations", `Error updating round ${roundNumber} data:`, error);
    }
    
    return { error };
  } catch (error) {
    debugLog(DebugLevel.ERROR, "databaseOperations", `Exception updating round ${roundNumber} data:`, error);
    return { error };
  }
};

/**
 * Validates that the necessary word data exists for a round
 */
export const validateRoundWordData = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string
) => {
  // First check if we have both words already
  if (userWord && aiWord) {
    return { 
      success: true, 
      userWord, 
      aiWord,
      aiExplanation: undefined
    };
  }
  
  // Otherwise fetch from database
  try {
    const { data, error } = await fetchRoundData(gameId, roundNumber);
    
    if (error || !data) {
      debugLog(DebugLevel.ERROR, "databaseOperations", `Error validating word data for round ${roundNumber}:`, error);
      return { 
        success: false, 
        userWord: null, 
        aiWord: null,
        aiExplanation: null
      };
    }
    
    // Use data from database if it exists, otherwise use the provided values
    const validatedUserWord = data.user_word || userWord;
    const validatedAiWord = data.ai_word || aiWord;
    
    // Check if we have valid words after combining data
    if (!validatedUserWord || !validatedAiWord) {
      debugLog(DebugLevel.ERROR, "databaseOperations", `Missing word data for round ${roundNumber} after validation`);
      return { 
        success: false, 
        userWord: null, 
        aiWord: null,
        aiExplanation: null
      };
    }
    
    return { 
      success: true, 
      userWord: validatedUserWord, 
      aiWord: validatedAiWord,
      aiExplanation: data.ai_explanation || undefined
    };
  } catch (error) {
    debugLog(DebugLevel.ERROR, "databaseOperations", `Exception validating word data for round ${roundNumber}:`, error);
    return { 
      success: false, 
      userWord: null, 
      aiWord: null,
      aiExplanation: null
    };
  }
};
